import React, { createContext, useContext, useState } from 'react'
import { MessageInstance } from 'antd/es/message/interface'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import {
  Registration,
  TeamOfficialType,
} from '@sportninja/common/types/Registration'
import { Country } from '@sportninja/common/hooks/useCountries'
import { Coupon } from '@sportninja/common/hooks/useCoupons'
import { RegistrationTableDivision } from 'src/pages/Schedule/PlayerRegistration/helpers'
import { Currency } from '@sportninja/common/types'

type AdminTeamRegisterState = {
  team: string
  team_official: string
  team_officials: {
    id?: string
    name: string
    email: string
    phone: string
    zipCode: string
    title: string
    country: string
    is_treasurer?: boolean
  }[]
  auto_approval: boolean
  complimentary: boolean
  cost_per_team: string | null
  deposit_amount: string | null
  fieldErrors: string[]
  customError: any
  loading: boolean
  error: string
  couponCode: string
  coupon: Coupon | null
}

export interface AdminTeamRegisterContextType {
  state: AdminTeamRegisterState
  setState: React.Dispatch<React.SetStateAction<AdminTeamRegisterState>>
  clearState: () => void
  onSubmit: () => Promise<void>
  messageApi: MessageInstance
  registration: Registration
  teamOfficialTypes: TeamOfficialType[]
  cost: string
  deposit: string
  singlePayment: boolean
  countries: Country[]
  divisionId: string
  costValue: number
  setCustomErroByField: (field: string, message: string) => void
  currency: Currency
}

const AdminTeamRegisterContext = createContext<
  AdminTeamRegisterContextType | undefined
>(undefined)

const useAdminTeamRegisterContext = () => {
  const context = useContext(AdminTeamRegisterContext)
  if (!context) {
    throw new Error(
      'useAdminTeamRegisterContext must be used within a AdminTeamRegisterContextWrapper'
    )
  }
  return context
}

type AdminTeamRegisterContextWrapperProps = {
  children: React.ReactNode
  messageApi: MessageInstance
  setIsFormOpen: React.Dispatch<React.SetStateAction<boolean>>
  isOpen: boolean
  divisionId: string
  refresh?: (refresh: boolean) => void
  scheduleId: string
  registration: Registration
  teamOfficialTypes: TeamOfficialType[]
  cost: string
  deposit: string
  singlePayment: boolean
  countries: Country[]
  costValue: number
  division: RegistrationTableDivision | null
  currency: Currency
}

const AdminTeamRegisterContextWrapper = ({
  children,
  messageApi,
  setIsFormOpen,
  divisionId,
  refresh = () => {},
  scheduleId,
  registration,
  teamOfficialTypes,
  cost,
  deposit,
  singlePayment,
  countries,
  costValue,
  division,
  currency,
}: AdminTeamRegisterContextWrapperProps) => {
  const [state, setState] = useState<AdminTeamRegisterState>({
    team: '',
    team_official: '',
    team_officials: [],
    auto_approval: false,
    complimentary: false,
    cost_per_team: null,
    deposit_amount: null,
    fieldErrors: [],
    customError: {},
    loading: false,
    error: '',
    couponCode: '',
    coupon: null,
  })

  const setCustomErroByField = (field: string, message: string) => {
    setState((prev) => ({ ...prev, fieldErrors: [...prev.fieldErrors, field] }))
    setState((prev) => ({
      ...prev,
      customError: { ...prev.customError, [field]: message },
    }))
  }

  const clearState = () => {
    setState({
      team: '',
      team_official: '',
      team_officials: [],
      auto_approval: false,
      complimentary: false,
      cost_per_team: null,
      deposit_amount: null,
      fieldErrors: [],
      customError: {},
      loading: false,
      error: '',
      couponCode: '',
      coupon: null,
    })
  }

  const onSubmit = async () => {
    try {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: '',
        fieldErrors: [],
        customError: {},
      }))
      const composeTeamOfficials = state.team_officials.map((official) => ({
        email: official.email,
        name_first: official.name.split(' ')[0],
        name_last: official.name.split(' ')[1] || '',
        phone: official.phone,
        team_official_type_id: official?.title
          ? Number(official.title)
          : teamOfficialTypes[0]?.id,
        is_treasurer: official.is_treasurer,
      }))
      // find the country
      const country = countries.find(
        (c) => String(c.id) === String(state.team_officials[0]?.country)
      )
      // /registration/{schedule}/admin/team
      await req(`/registration/${scheduleId}/admin/team`, {
        method: 'POST',
        body: JSON.stringify({
          team_id: state.team,
          schedule_registration_id: division?.registration?.id,
          coupon_code_id: state.coupon?.id,
          is_deposit_required: state.auto_approval ? false : true,
          is_comp: state.complimentary,
          country_code: country?.iso_3166_2,
          postal_code: state.team_officials[0]?.zipCode,
          officials: composeTeamOfficials,
        }),
      })
      setState((prev) => ({ ...prev, loading: false }))
      messageApi.open({
        type: 'success',
        content: 'Team registered successfully',
        duration: 5,
      })
      await refresh(false)
      setIsFormOpen(false)
      clearState()
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      setState((prev) => ({ ...prev, error: errorMessage, loading: false }))
      messageApi.open({
        type: 'error',
        content: errorMessage,
        duration: 5,
      })
    }
  }

  const value = {
    state,
    setState,
    clearState,
    onSubmit,
    messageApi,
    registration,
    teamOfficialTypes,
    cost,
    deposit,
    singlePayment,
    countries,
    divisionId,
    costValue,
    setCustomErroByField,
    currency,
  }

  return (
    <AdminTeamRegisterContext.Provider value={value}>
      {children}
    </AdminTeamRegisterContext.Provider>
  )
}

export {
  AdminTeamRegisterContext,
  useAdminTeamRegisterContext,
  AdminTeamRegisterContextWrapper,
}
